import axios from "axios";
import { getTokens, authEvents } from "./index";
import { auth } from "@startupp-cloud/startupp-ai-sdk";

// Helper function to get organization credentials
const getOrganizationCredentials = () => {
  // BYPASS SDK CONFIG - SDK is truncating the API key!
  // Use environment variables directly instead of SDK's getConfig()
  const credentials = {
    organizationId: process.env.REACT_APP_DEFAULT_ORGANIZATION_ID,
    apiKey: process.env.REACT_APP_APIKEY,
  };

  console.log(
    "[Auth] Using environment variables directly (SDK truncates API key):",
    {
      organizationId: credentials.organizationId ? "SET" : "NOT SET",
      apiKey: credentials.apiKey ? "SET" : "NOT SET",
      apiKeyLength: credentials.apiKey ? credentials.apiKey.length : 0,
      apiKeyStart: credentials.apiKey
        ? credentials.apiKey.substring(0, 10)
        : "N/A",
      apiKeyEnd: credentials.apiKey
        ? credentials.apiKey.substring(credentials.apiKey.length - 6)
        : "N/A",
      envVarName: "REACT_APP_APIKEY",
    }
  );

  return credentials;
};

/**
 * Sets up authentication interceptors for axios
 * This is to be called once during application initialization
 */
export const setupAuthInterceptors = () => {
  // Request interceptor to add Authorization header
  axios.interceptors.request.use(
    (config) => {
      // Use SDK singleton to get tokens instead of local storage
      const tokens = auth.getTokens();
      const credentials = getOrganizationCredentials();

      if (tokens && tokens.accessToken) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${tokens.accessToken}`;
        console.log("[Auth] Adding auth header to request:", config.url);
      } else {
        console.log("[Auth] No access token available in SDK singleton for request:", config.url);
      }

      // Add organization headers
      if (credentials.organizationId && credentials.apiKey) {
        config.headers = config.headers || {};
        config.headers["X-Organization-Id"] = credentials.organizationId;
        config.headers["X-API-Key"] = credentials.apiKey;

        // Debug: Log the actual values being sent (safely)
        console.log("[Auth] Setting headers:", {
          "X-Organization-Id": credentials.organizationId
            ? `${credentials.organizationId.substring(0, 8)}...`
            : "NOT SET",
          "X-API-Key": credentials.apiKey
            ? `${credentials.apiKey.substring(
                0,
                8
              )}...${credentials.apiKey.substring(
                credentials.apiKey.length - 4
              )}`
            : "NOT SET",
          "X-API-Key-Length": credentials.apiKey
            ? credentials.apiKey.length
            : 0,
          "X-API-Key-StartsWith-sk_live": credentials.apiKey
            ? credentials.apiKey.startsWith("sk_live_")
            : false,
        });

        console.log(
          "[Auth] Adding organization headers to request:",
          config.url
        );
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Keep track of requests that are being retried
  const pendingRetryRequests = new Set();

  // Response interceptor to handle auth errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const requestId = `${originalRequest.method}:${originalRequest.url}`;

      // Check if we're in the process of logging out, and if so, don't try to handle auth errors
      const isLoggingOut = sessionStorage.getItem("loggingOut") === "true";

      if (isLoggingOut && originalRequest.url.includes("/auth/logout")) {
        console.log(
          "[Auth][Interceptor] Ignoring error during logout API call"
        );
        return Promise.resolve(); // Just resolve instead of retrying during logout
      }

      // Only log non-auth errors or in development
      if (
        !error.response ||
        (error.response.status !== 401 && error.response.status !== 403)
      ) {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[Auth][Interceptor] Response error:",
            error,
            "RequestId:",
            requestId
          );
        }
      } else {
        // Handle 401/403 errors silently in production
        if (process.env.NODE_ENV === "development") {
          console.log(
            "[Auth][Interceptor] Auth error detected, handling silently"
          );
        }
      }

      // Handle auth errors (401, 403)
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403) &&
        !pendingRetryRequests.has(requestId) &&
        !originalRequest._retry &&
        !isLoggingOut // Don't retry if we're logging out
      ) {
        originalRequest._retry = true;
        
        // Use SDK singleton to get refresh token
        const tokens = auth.getTokens();
        const refreshToken = tokens ? tokens.refreshToken : null;

        if (process.env.NODE_ENV === "development") {
          console.log(
            "[Auth][Interceptor] 401/403 detected. refreshToken:",
            refreshToken ? "present" : "absent"
          );
        }

        if (refreshToken) {
          pendingRetryRequests.add(requestId);

          try {
            console.log(
              "[Auth][Interceptor] Token expired, triggering re-authentication for:",
              requestId
            );

            // Clear the expired tokens from SDK singleton
            auth.clearTokens();

            // Trigger re-authentication
            authEvents.emit("unauthorized", {
              error,
              request: originalRequest,
            });
            authEvents.emit("redirectToLogin");

            pendingRetryRequests.delete(requestId);

            return Promise.reject({
              ...error,
              isAuthError: true,
              message: "Authentication required",
            });
          } catch (refreshError) {
            pendingRetryRequests.delete(requestId);
            console.error("[Auth][Interceptor] Re-auth failed:", refreshError);

            // Check if we're currently logging out
            const isLoggingOut =
              sessionStorage.getItem("loggingOut") === "true";

            if (!isLoggingOut) {
              console.log(
                "[Auth][Interceptor] Triggering redirectToLogin event"
              );
              // Emit redirectToLogin event to force proper login redirection
              authEvents.emit("redirectToLogin", {
                error,
                request: originalRequest,
              });
            } else {
              console.log(
                "[Auth][Interceptor] Skipping redirect during logout flow"
              );
            }

            // Redirect to login page
            authEvents.emit("redirectToLogin");

            return Promise.reject({ ...error, isAuthError: true });
          }
        } else {
          console.log(
            "[Auth][Interceptor] No refresh token available in SDK singleton, redirecting to login"
          );

          pendingRetryRequests.add(requestId);

          // Emit unauthorized event to trigger logout
          authEvents.emit("unauthorized", { error, request: originalRequest });

          // Redirect to login page
          authEvents.emit("redirectToLogin");

          setTimeout(() => {
            pendingRetryRequests.delete(requestId);
          }, 5000);

          return Promise.reject({
            ...error,
            isAuthError: true,
            originalRequest,
          });
        }
      }

      return Promise.reject(error);
    }
  );

  console.log("[Auth] Auth interceptors set up successfully");
};

/**
 * Creates an axios instance that won't get caught in the auth interceptors
 * Useful for auth-related API calls
 */
export const createAuthAxiosInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
  });

  // Add request interceptor to add auth header, but no response interceptor
  instance.interceptors.request.use(
    (config) => {
      // Use SDK singleton to get tokens
      const tokens = auth.getTokens();
      const credentials = getOrganizationCredentials();

      if (tokens && tokens.accessToken) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${tokens.accessToken}`;
      }

      // Add organization headers
      if (credentials.organizationId && credentials.apiKey) {
        config.headers = config.headers || {};
        config.headers["X-Organization-Id"] = credentials.organizationId;
        config.headers["X-API-Key"] = credentials.apiKey;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
};
